import { MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StandAloneLink, Surface, Txt } from '@vst/beam';
import { Close, Edit } from '@vst/beam-icons/icons';
import { priceDecimalFormat } from '@buy-viasat/utils';
import { CartProduct } from '@buy-viasat/types/build/bv';
import { appActions, Modals, selectCurrencySymbol } from '@buy-viasat/redux/src/app';
import { cartActions, selectOptionalServicesEditMode } from '@buy-viasat/redux/src/cart';
import { addonActions, selectLoadingAddonId } from '@buy-viasat/redux/src/addons';
import { selectLocale } from '@buy-viasat/redux/src/app';
import { TrackingActions, TrackingCategory } from '@buy-viasat/redux/src/analytics';

import { Routes, selectCurrentAppRoute } from '@buy-viasat/redux/src/navigator';
import CheckoutCartOptionalServicesItem from '../CheckoutCartOptionalServicesItem';
import { SkeletonLoader } from '../../SkeletonLoader';

import { AddonItem } from '../types';
import { useTrackEvent } from '../../../hooks';

import styles from './checkoutCartOptionalServices.module.scss';

export type CheckoutCartOptionalServicesProps = {
  translations: {
    edit: string;
    free: string;
    optionalServices: string;
    priceAfterPromotion: (price: string, duration: string) => string;
    priceWithMonth?: (price: string) => string;
  };
  addonItems: AddonItem[];
  selectedAddonsTotal: number;
};

const CheckoutCartOptionalServices = ({
  translations,
  addonItems,
  selectedAddonsTotal,
}: CheckoutCartOptionalServicesProps) => {
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();

  const locale = useSelector(selectLocale);
  const isEditMode = useSelector(selectOptionalServicesEditMode);
  const currencySymbol = useSelector(selectCurrencySymbol);
  const currentRoute = useSelector(selectCurrentAppRoute);

  const checkValue = (value: number | undefined | null) => value ?? 0;
  const loadingAddonId = useSelector(selectLoadingAddonId);
  const visibleAddonItems = addonItems.filter((addon) => !addon.shouldHideBundleAddon);
  visibleAddonItems.sort((a, b) => checkValue(a.displayOrder) - checkValue(b.displayOrder));

  const handleEditClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    dispatch(cartActions.setOptionalServicesEditMode(!isEditMode));
  };

  const handleRemoveClick = (addon: CartProduct) => () => {
    trackEvent(TrackingCategory.CART, TrackingActions.REMOVED_ADDON, { label: addon.name });
    if (addon.productTypeId) {
      if (currentRoute === Routes.ADDONS) {
        //if a customer is on the addons page and they decide to add/remove an addon, the client does not actually call any API until the customer clicks on the 'Continue button
        //therefore we need to make sure that if a customer removes an addon from their cart while on the addons page, that addon will not be sent for upsert(ion?)
        dispatch(addonActions.removeAddonIdToUpsert(addon.productTypeId));
      }
      dispatch(addonActions.setAddonIdForRemoval(addon.productTypeId));
      dispatch(appActions.setModalVisible(Modals.REMOVE_OPTIONAL_SERVICE));
    }
  };

  const shouldShowTotal = visibleAddonItems.length > 1 && selectedAddonsTotal > 0;
  const hasOnlyBundledAddons = visibleAddonItems.every((addon) => addon.isBundle);
  const shouldShowEditButton = visibleAddonItems.length >= 1 && !hasOnlyBundledAddons;

  return (
    <Surface variant="secondary" p="16px" mt="8px" data-testid="cartOptionalServices" radius="8px">
      <Surface variant="secondary" data-testid="cartOptionalServicesHeading" className={styles['heading']}>
        <Txt variant="heading6" data-testid="cartOptionalServicesTitle">
          {translations.optionalServices}
        </Txt>
        {shouldShowEditButton && (
          <StandAloneLink
            href="#"
            linkSize="small"
            onClick={handleEditClick}
            className={loadingAddonId ? styles['editBtnDisabled'] : styles['editBtn']}
            icon={isEditMode ? Close : Edit}
            data-testid="cartOptionalServicesEditButton"
          >
            {translations.edit}
          </StandAloneLink>
        )}
      </Surface>
      {loadingAddonId ? (
        <div className={styles['loader']} data-testid="cartOptionalServicesLoader">
          <Surface data-testid="cartOptionalServicesItemLoader" mt={'8px'} variant="secondary">
            <SkeletonLoader characters={10} height={'sm'} />
          </Surface>
          <Surface variant='secondary' mt={'8px'}>
            <Surface variant='secondary'>
              <SkeletonLoader characters={5} height={'sm'} />
            </Surface>
          </Surface>
        </div>

      ) : (
        <Surface variant="secondary" data-testid="cartOptionalServicesList">
          {visibleAddonItems.map((addon) => (
            <CheckoutCartOptionalServicesItem
              addon={addon}
              isEditMode={isEditMode}
              key={addon?.productTypeId}
              onRemove={handleRemoveClick(addon)}
            />
          ))}
        </Surface>
      )}
      {shouldShowTotal && (
        <Surface
          mt="8px"
          pt="8px"
          variant="secondary"
          className={styles['total']}
          data-testid="cartOptionalServicesTotal"
        >
          <Txt variant="bodySmallBold">{priceDecimalFormat(selectedAddonsTotal, locale, currencySymbol, 2)}</Txt>
        </Surface>
      )}
    </Surface>
  );
};

export default CheckoutCartOptionalServices;
